import { useCallback, useEffect, useMemo, useState } from 'react'

import { getSalesLensCategories } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { IAbstractLensItem } from '@/types'
import { SalesLensCategoryModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'
import { DateRange } from 'react-day-picker'

export default function useSalesLensCategories(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [categoriesData, setCategoriesData] =
    useState<SalesLensCategoryModel[]>()
  const [trendCategoriesData, setTrendCategoriesData] =
    useState<SalesLensCategoryModel[]>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { data } = await fetchCategoriesData(branches, startDate, endDate)
        setCategoriesData(data)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches, startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange
        const { data } = await fetchCategoriesData(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to')
        )
        setTrendCategoriesData(data)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches, startDate, endDate, dateFilterType])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching sales lens categories...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  // Format categories data for display with trend data
  const formattedCategoriesData = useMemo(() => {
    if (!categoriesData || !trendCategoriesData) return []

    // Create a map of trend data by category name for easy lookup
    const trendDataMap = new Map(
      trendCategoriesData.map((category) => [category.subSubCategory, category])
    )

    return categoriesData.map(
      (category: SalesLensCategoryModel): IAbstractLensItem<any> => {
        const trendCategory = trendDataMap.get(category.subSubCategory)

        return {
          item: category.subSubCategory,
          current: {
            netSalesAmount: category.netSalesAmount,
            netSalesPercentage: category.netSalesPercentage,
            unitSold: category.unitSold,
            avgSellingPrice: category.avgSellingPrice,
            grossToDiscountPercentage: category.grossToDiscountPercentage,
          },
          prev: trendCategory
            ? {
                netSalesAmount: trendCategory.netSalesAmount,
                netSalesPercentage: trendCategory.netSalesPercentage,
                unitSold: trendCategory.unitSold,
                avgSellingPrice: trendCategory.avgSellingPrice,
                grossToDiscountPercentage:
                  trendCategory.grossToDiscountPercentage,
              }
            : undefined,
        }
      }
    )
  }, [categoriesData, trendCategoriesData])

  return {
    formattedCategoriesData,
    loading,
    error,
    refetch,
  }
}

async function fetchCategoriesData(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  data: SalesLensCategoryModel[]
}> {
  try {
    const response = await getSalesLensCategories(branches, startDate, endDate)

    const data: SalesLensCategoryModel[] = response.map((responseItem) => {
      return convertData(
        responseItem.sub_sub_category,
        responseItem.net_sales_amount,
        responseItem.net_sales_percentage,
        responseItem.quantity_sold,
        responseItem.gross_to_discount_percentage
      )
    })

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  subSubCategory: string,
  netSalesAmount: number,
  netSalesPercentage: number,
  quantitySold: number,
  grossToDiscountPercentage: number
): SalesLensCategoryModel {
  return {
    subSubCategory,
    netSalesAmount: formatMetric(netSalesAmount, 2),
    netSalesPercentage: formatMetric(netSalesPercentage, 2),
    unitSold: formatMetric(quantitySold, 2),
    avgSellingPrice: formatMetric(netSalesAmount / quantitySold, 2),
    grossToDiscountPercentage: formatMetric(grossToDiscountPercentage, 2),
  }
}
